import { useSelector } from 'react-redux';
import { DEFAULT_THEME_MODE, ThemeMode, guidePalettes } from './guideTokens';

/**
 * Colores para gráficos (echarts, react-charting, etc.) según el tema activo de la guía de diseño.
 * Los gráficos no leen tokens de Fluent ni variables CSS, por eso reciben los colores resueltos.
 */
export interface ChartPalette {
  /** Serie de colores categóricos, en orden de uso. */
  series: string[];
  /** Semánticos: cobrado / ingreso / ok. */
  positive: string;
  /** Semánticos: pagado / egreso / alerta. */
  negative: string;
  warning: string;
  confirmed: string;
  text: string;
  textSecondary: string;
  axisLine: string;
  gridLine: string;
  background: string;
  surface: string;
  tooltipBackground: string;
  tooltipText: string;
}

const SERIES: Record<ThemeMode, string[]> = {
  light: ['#1976d2', '#e8913a', '#2e7d32', '#5e35b1', '#a14f05', '#0d47a1', '#5b6675', '#c62828'],
  dark: ['#64b5f6', '#ffb74d', '#81c995', '#b39ddb', '#ff8a65', '#1976d2', '#a0a8b3', '#e8eaed'],
  'high-contrast': ['#ffe600', '#ffb13b', '#5cff8d', '#e0b8ff', '#ff7a7a', '#80d8ff', '#ffffff', '#e6e6e6'],
};

export const getChartPalette = (mode: ThemeMode = DEFAULT_THEME_MODE): ChartPalette => {
  const p = guidePalettes[mode] ?? guidePalettes[DEFAULT_THEME_MODE];
  const series = SERIES[mode] ?? SERIES[DEFAULT_THEME_MODE];
  return {
    series,
    positive: series[0],
    negative: series[1],
    warning: p.warning,
    confirmed: p.confirmed,
    text: p.text,
    textSecondary: p.textSecondary,
    axisLine: p.border,
    gridLine: mode === 'high-contrast' ? '#4d4d4d' : p.border,
    background: p.background,
    surface: p.surface,
    tooltipBackground: p.tooltipBackground,
    tooltipText: p.tooltipText,
  };
};

interface ThemeSlice {
  Theme?: { mode?: ThemeMode };
}

/** Paleta del tema activo (se recalcula cuando el usuario cambia de tema). */
export const useChartPalette = (): ChartPalette =>
  getChartPalette(useSelector((state: ThemeSlice) => state.Theme?.mode ?? DEFAULT_THEME_MODE));
